import { Injectable } from '@angular/core';
import { FILE_UPLOAD_CHUNK_SIZE, LogDocumentType } from '@caballus/common';
import {
    CapacitorPluginService,
    GalleryService,
    LogCreateDto,
    LogService,
    Media,
    MediaDocumentType,
    MediaService,
    MediaUploadItem,
    NetworkStatusService
} from '@caballus/ui-common';
import { FileSelectorPlugin } from '@ion-caballus/core/plugins';
import { VideoService } from '@ion-caballus/core/services/video.service';
import { bytesToSize, getChunkEnd } from '@ion-caballus/core/util';
import { Platform } from '@ionic/angular';
import { Store } from '@ngxs/store';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, combineLatest, from, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import {
    RemoveItemFromQueueAction,
    SetCurrentUploadingItemAction,
    UpdateItemInQueueAction
} from '../state/actions';
import { StorageService } from './storage.service';

declare let cordova: any;

enum Keys {
    MediaUpload = 'media-upload-queue-'
}

@Injectable({
    providedIn: 'root'
})
export class MediaUploadQueueService {
    private _isSyncInProcess$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private _currentMediaId$: BehaviorSubject<string> = new BehaviorSubject<string>('');
    private _currentMediaSubjectId$: BehaviorSubject<string> = new BehaviorSubject<string>('');
    public successfulSync$: BehaviorSubject<string> = new BehaviorSubject<string>('');

    constructor(
        private readonly _storageService: StorageService,
        private readonly _mediaService: MediaService,
        private readonly _galleryService: GalleryService,
        private readonly _capacitorPluginService: CapacitorPluginService,
        private readonly _toastService: ToastrService,
        private readonly _videoService: VideoService,
        private readonly _store: Store,
        private readonly _platform: Platform,
        private readonly _logService: LogService,
        private readonly _networkStatusService: NetworkStatusService
    ) {}

    public get isSyncInProcess(): boolean {
        return this._isSyncInProcess$.getValue();
    }

    public remove(itemId: string): Observable<void> {
        return this._store.dispatch(new RemoveItemFromQueueAction(itemId)).pipe(map(() => void 0));
    }

    public peek(): Observable<MediaUploadItem> {
        return this.getAll().pipe(map(items => (items ? items[0] : null)));
    }

    public sync(): Observable<unknown> {
        return this.peek().pipe(
            // takeUntil(this._syncUntil()),
            switchMap(mediaItem => {
                if (!mediaItem) {
                    /**
                     * As soon as we have nothing to upload, turn off background mode.
                     */
                    if (this._platform.is('cordova')) {
                        cordova.plugins.backgroundMode.disable();
                        cordova.plugins.backgroundMode.setEnabled(false);
                    }

                    if (this._isSyncInProcess$.getValue()) {
                        // reset the isSyncInProcess$
                        this._isSyncInProcess$.next(false);
                    }

                    // reset the current uploading item
                    this._store.dispatch(new SetCurrentUploadingItemAction(''));
                    return of(undefined);
                }

                const uploadItem: MediaUploadItem = { ...mediaItem };
                this._isSyncInProcess$.next(true);
                // set the current uploading item
                this._store.dispatch(new SetCurrentUploadingItemAction(uploadItem.mediaId));
                this._currentMediaId$.next(uploadItem.mediaId);
                this._currentMediaSubjectId$.next(uploadItem.mediaSubjectId);

                /**
                 * As background mode will slow down app performance,
                 * it should only be turned on when there are items in the media upload queue
                 * that need to be uploaded.
                 */
                /**
                 * As soon as we have nothing to upload, turn off background mode.
                 */
                if (this._platform.is('cordova')) {
                    cordova.plugins.backgroundMode.enable();
                    cordova.plugins.backgroundMode.setEnabled(true);
                }

                let observable: Observable<Partial<MediaUploadItem>>;
                if (
                    uploadItem.mediaType === MediaDocumentType.Image ||
                    uploadItem.mediaType === MediaDocumentType.Document
                ) {
                    if (uploadItem.isMediaCreatedOnBackend) {
                        observable = this._uploadMedia(uploadItem);
                    } else {
                        observable = this._prepareMediaForUpload(uploadItem);
                    }
                } else {
                    if (uploadItem.isMediaCreatedOnBackend) {
                        observable = this._uploadVideoMedia(uploadItem);
                    } else {
                        observable = this._prepareVideoMediaForUpload(uploadItem);
                    }
                }

                return observable.pipe(
                    switchMap(res => this._completeUpload(res).pipe(map(() => res))),
                    tap(res => this.successfulSync$.next(res.fileName)),
                    switchMap(() => this.sync()),
                    catchError(err => {
                        // when upload link expires, try to refresh the link again
                        if (err.message === 'FileSelectorPlugin: Link expired!') {
                            return this._mediaService
                                .refreshUploadLinks(
                                    uploadItem.uploadId,
                                    uploadItem.uploadToken,
                                    uploadItem.fileSize
                                )
                                .pipe(
                                    switchMap(uploadLinks =>
                                        // update uploadLinks
                                        this.updateItem({
                                            mediaId: uploadItem.mediaId,
                                            uploadLinks
                                        }).pipe(
                                            switchMap(() =>
                                                // start the sync process again
                                                this.sync().pipe(
                                                    catchError(err1 => {
                                                        this._isSyncInProcess$.next(false);
                                                        // Display/log error
                                                        this._toastService.error(
                                                            'Unable to upload file due to expired link! It has been removed from the queue, please try again or contact support.'
                                                        );

                                                        // log error
                                                        this._logErrorData(
                                                            uploadItem,
                                                            err,
                                                            err1,
                                                            'MediaUploadQueueService > _completeUpload() > sync() > refreshUploadLinks()'
                                                        );

                                                        // TODO: work on this
                                                        // cleanup item
                                                        this._galleryService
                                                            .deleteGalleryMedia({
                                                                horseId: uploadItem.mediaSubjectId,
                                                                mediaId: uploadItem.mediaId
                                                            })
                                                            .subscribe();
                                                        this.remove(uploadItem.mediaId);
                                                        throw err1;
                                                    })
                                                )
                                            )
                                        )
                                    )
                                );
                        } else if (err.message === 'FileSelectorPlugin: File not found!') {
                            this._toastService.error(
                                'Upload file not found! Please try again or contact support.'
                            );
                            // reset the isSyncInProcess$
                            this._isSyncInProcess$.next(false);

                            // log error
                            this._logErrorData(
                                uploadItem,
                                err,
                                null,
                                'MediaUploadQueueService > _completeUpload() > sync() > FileSelectorPlugin: File not found!'
                            );

                            // remove item from queue if file is not available
                            this._galleryService
                                .deleteGalleryMedia({
                                    horseId: uploadItem.mediaSubjectId,
                                    mediaId: uploadItem.mediaId
                                })
                                .subscribe();
                            this.remove(uploadItem.mediaId);

                            throw err;
                        } else {
                            // NOTE this else {} block is hit when the this._completeUpload() method errors out for ANY REASON other than the two reasons above
                            // reset the isSyncInProcess$
                            this._isSyncInProcess$.next(false);
                            const toastConfig = {
                                message: `Unable to upload file! It has been removed from the queue, please try again or contact customer support.`,
                                duration: 10000
                            };
                            this._toastService.error(toastConfig.message, '', {
                                timeOut: toastConfig.duration
                            });

                            // log error
                            this._logErrorData(
                                uploadItem,
                                err,
                                null,
                                'MediaUploadQueueService > _completeUpload() > sync() > generic else{} block'
                            );

                            // cleanup item
                            this._galleryService
                                .deleteGalleryMedia({
                                    horseId: uploadItem.mediaSubjectId,
                                    mediaId: uploadItem.mediaId
                                })
                                .subscribe();
                            // remove item from queue if unable to upload, to allow user to retry
                            this.remove(uploadItem.mediaId);

                            throw err;
                        }
                    })
                );
            }),
            catchError(err => {
                // NOTE this catchError() block is hit when the observable media item errors out for ANY REASON before attempting to complete the upload
                // we log after each call when trying to create observable so we don't need to here, but we do need to clean up the item and tell user
                this._isSyncInProcess$.next(false);
                throw err;
            })
        );
    }

    public getAll(): Observable<MediaUploadItem[]> {
        return this._store.selectOnce(s => s.mediaUploadQueue.mediaFileUploadQueue);
    }

    public async getAllFromStorage(): Promise<MediaUploadItem[]> {
        try {
            const items = await this._storageService.getUserData<MediaUploadItem[]>(
                Keys.MediaUpload,
                false
            );
            return Array.isArray(items) ? items.map(e => new MediaUploadItem(e)) : [];
        } catch (e) {}
    }

    public updateItem(updatedItem: Partial<MediaUploadItem>): Observable<void> {
        return this._store.dispatch(new UpdateItemInQueueAction(updatedItem));
    }

    // update queue
    public async updateQueue(items: MediaUploadItem[]): Promise<void> {
        try {
            await this._storageService.setUserData(Keys.MediaUpload, JSON.stringify(items));
        } catch (e) {}
    }

    private _syncUntil(): Observable<boolean> {
        return combineLatest([
            this._networkStatusService.networkStatus$,
            this._capacitorPluginService.networkStatus(),
            this._store.select(s => s.user.user.settings.uploadUsingCellularData)
        ]).pipe(
            map(([connection, network, allowCellularUpload]) => {
                if (!connection.isOnline) {
                    return false;
                }
                return allowCellularUpload ? true : network.connectionType === 'wifi';
            })
        );
    }

    private _prepareMediaForUpload(item: MediaUploadItem): Observable<Partial<MediaUploadItem>> {
        return this._getOrCreateMedia(item).pipe(
            map((media: Media) => {
                if (media.thumbnail) {
                    item.thumbnailUrl = media.thumbnail.url;
                }
                item.isMediaCreatedOnBackend = true;
                return this.updateItem(item);
            }),
            switchMap(() => this._uploadMedia(item))
        );
    }

    private _uploadMedia(item: MediaUploadItem): Observable<Partial<MediaUploadItem>> {
        return this._getBlob(item.fileName, item.mediaType, false).pipe(
            switchMap(blob => this._mediaService.completeMediaUpload(item.mediaId, blob)),
            map((): Partial<MediaUploadItem> => item),
            catchError(err => {
                this._toastService.error('Media upload failed!');
                throw err;
            })
        );
    }

    private _prepareVideoMediaForUpload(
        item: MediaUploadItem
    ): Observable<Partial<MediaUploadItem>> {
        return this._getOrCreateMedia(item).pipe(
            tap(() => {
                item.isMediaCreatedOnBackend = true;
            }),
            switchMap(() => {
                if (item.isFileMovedToPermanentStorage) {
                    return of(item);
                }

                return this._videoService.moveVideoToThePermanentLocation(item).pipe(
                    map(({ newPath }) => {
                        // update filePath and set isFileMovedToPermanentStorage to true
                        item.filePath = newPath;
                        item.isFileMovedToPermanentStorage = true;
                        return this.updateItem(item);
                    }),
                    catchError(err => {
                        // log error
                        this._logErrorData(
                            item,
                            err,
                            null,
                            'MediaUploadQueueService > sync() > _prepareVideoMediaForUpload() > _getBlob() > _mediaService.createMedia() > _videoService.moveRideVideoToThePermanentLocation()'
                        );

                        throw err;
                    })
                );
            }),
            switchMap(() => this._initiateResumableUpload(item)),
            switchMap(updatedItem => this._uploadVideoMedia(updatedItem))
        );
    }

    private _uploadVideoMedia(item: MediaUploadItem): Observable<Partial<MediaUploadItem>> {
        let obv: Observable<MediaUploadItem> = null;
        if (!item.uploadId) {
            obv = this._initiateResumableUpload(item);
        } else {
            obv = of(item);
        }

        return obv.pipe(
            switchMap(updatedItem =>
                from(
                    FileSelectorPlugin.UploadVideoFile({
                        filePath: updatedItem.filePath,
                        uploadLink: updatedItem.uploadLinks.at(updatedItem.lastUploadedPart + 1),
                        lastUploadedPart: updatedItem.lastUploadedPart,
                        mediaId: updatedItem.mediaId,
                        fileSize: updatedItem.fileSize,
                        start: updatedItem.uploadedBytes,
                        end: getChunkEnd(
                            updatedItem.uploadedBytes,
                            FILE_UPLOAD_CHUNK_SIZE,
                            updatedItem.fileSize
                        )
                    })
                )
            ),
            map(res => ({ ...item, ...res })),
            catchError(err => {
                // log error
                this._logErrorData(
                    item,
                    err,
                    null,
                    'MediaUploadQueueService > sync() > _uploadVideoMedia() > FileSelectorPlugin.UploadVideoFile()'
                );

                throw err;
            })
        );
    }

    private _initiateResumableUpload(item: MediaUploadItem): Observable<MediaUploadItem> {
        return this._mediaService.initiateResumableUpload(item.fileSize).pipe(
            map(res => {
                if (res.uploadId && res.uploadToken && res.uploadLinks && res.jwPlayerId) {
                    item.uploadId = res.uploadId;
                    item.uploadToken = res.uploadToken;
                    item.uploadLinks = res.uploadLinks;
                    item.jwPlayerId = res.jwPlayerId;

                    this.updateItem(item);
                    return item;
                } else {
                    throw `Unable to initiate resumable upload! \n ${res}`;
                }
            }),
            catchError(err => {
                // log error
                this._logErrorData(
                    item,
                    err,
                    null,
                    'MediaUploadQueueService > sync() > _prepareVideoMediaForUpload() > _getBlob() > _mediaService.createMedia() > _videoService.moveRideVideoToThePermanentLocation() > _mediaService.initiateResumableUpload()'
                );

                throw err;
            })
        );
    }

    private _getBlob(
        fileName: string,
        mediaType: MediaDocumentType,
        thumbnail: boolean
    ): Observable<Blob> {
        return from(this._storageService.getBlob(fileName, mediaType, thumbnail));
    }

    private _completeUpload(updatedItem: Partial<MediaUploadItem>): Observable<void> {
        return this.getAll().pipe(
            map(items => items.find(item => item.mediaId === updatedItem.mediaId)),
            switchMap(matchedItem => {
                if (matchedItem) {
                    // verify if file is completely uploaded
                    const isFileUploaded =
                        matchedItem.mediaType === MediaDocumentType.Image ||
                        matchedItem.mediaType === MediaDocumentType.Document
                            ? true
                            : updatedItem.uploadedBytes >= matchedItem.fileSize;

                    // prepare object to update
                    const objToUpdate: Partial<MediaUploadItem> = {
                        ...matchedItem,
                        ...updatedItem,
                        lastUploadedPart: matchedItem.lastUploadedPart + 1,
                        uploadedFileSizeInString: bytesToSize(updatedItem.uploadedBytes)
                    };

                    // verify if file is uploaded
                    if (isFileUploaded) {
                        if (matchedItem.mediaType === MediaDocumentType.Video) {
                            return this.updateItem(objToUpdate).pipe(
                                switchMap(() =>
                                    // complete the upload
                                    this._mediaService.completeResumableUpload(
                                        objToUpdate.uploadId,
                                        objToUpdate.uploadToken,
                                        objToUpdate.mediaId,
                                        objToUpdate.jwPlayerId
                                    )
                                ),
                                switchMap(() =>
                                    // remove file from the device
                                    from(
                                        FileSelectorPlugin.RemoveVideoFile({
                                            filePath: objToUpdate.filePath
                                        })
                                    )
                                ),
                                switchMap(() =>
                                    // remove file from the queue
                                    this.remove(objToUpdate.mediaId)
                                )
                            );
                        } else {
                            // remove file from the queue
                            return this.remove(objToUpdate.mediaId);
                        }
                    } else {
                        // update the item in the queue
                        return this.updateItem(objToUpdate);
                    }
                }
                return of(null);
            }),
            catchError(err => {
                return of(err);
            })
        );
    }

    private getMediaById(mediaId: string): Observable<Media> {
        return this._mediaService.getMediaById(mediaId);
    }

    private _getOrCreateMedia(item: MediaUploadItem): Observable<Media> {
        return this._getBlob(item.fileName, item.mediaType, true).pipe(
            switchMap(blob =>
                this.getMediaById(item.mediaId).pipe(
                    switchMap(media => {
                        if (media) {
                            return of(media);
                        }

                        return this._mediaService.createMedia(
                            item.mediaId,
                            item.mediaSubjectId,
                            blob,
                            item.mediaType,
                            item.mediaCollectionName,
                            item.filePath,
                            item.fileName
                        );
                    })
                )
            )
        );
    }

    private _logErrorData(uploadItem: MediaUploadItem, err, err1, location: string) {
        // log error
        const logData = {
            affectedUser: uploadItem.mediaSubjectId,
            type: LogDocumentType.Media,
            context: {
                error:
                    typeof err === 'string'
                        ? err
                        : JSON.stringify({
                              error: err,
                              message: err && err.error ? err.error.message : err
                          }),
                error2:
                    typeof err1 === 'string'
                        ? err1
                        : JSON.stringify({
                              error: err1,
                              message: err1 && err1.error ? err1.error.message : err1
                          }),
                data: JSON.stringify(uploadItem),
                location
            }
        };
        const data = new LogCreateDto(logData);
        this._logService.createLog(data).subscribe(() => {});
    }
}
